import type { ReactNode } from 'react'

/**
 * O número que a pessoa veio ver.
 *
 * ## Por que é um componente, e não uma classe
 *
 * Porque ele carrega uma **regra de contraste**, não só uma fonte. Número
 * grande sobre vidro é o lugar exato onde o Liquid Glass falha: o fundo se
 * move atrás do algarismo e a leitura vai junto. A resposta deste desenho é
 * estrutural — uma placa opaca atrás do texto (a `plaquinha`) — e deixá-la a
 * cargo de quem usa garante que um dia alguém esqueça.
 *
 * `sobreVidro` existe porque a placa é **errada** quando o fundo é previsível:
 * ali ela vira um retângulo mais escuro sem motivo. Quem sabe o que passa atrás
 * é quem compõe a tela.
 *
 * **Hoje nenhuma tela a liga**, e a razão é a decisão 205: o fundo do app virou
 * sólido (halo + grão, sem uma linha), então o risco de contraste que ela
 * resolvia deixou de existir. Ela continua aqui para o caso que ainda vai
 * aparecer — vidro sobre **conteúdo** que rola, como um cabeçalho grudado sobre
 * uma lista, onde o que passa atrás é imprevisível de novo.
 */
export function Numero({
  children,
  sobreVidro = false,
  tom = 'texto',
  tamanho = 'grande',
  className = '',
  ...rest
}: {
  children: ReactNode
  /** Põe a plaquinha atrás. Ligue quando o pai for `lens`, `prisma` ou `frost`. */
  sobreVidro?: boolean
  tom?: 'texto' | 'positivo' | 'negativo' | 'economia' | 'pendente'
  tamanho?: 'grande' | 'medio' | 'pequeno'
  className?: string
  [prop: string]: unknown
}) {
  return (
    <span
      {...rest}
      className={`display ${TAMANHO[tamanho]} ${sobreVidro ? 'plaquinha' : ''} ${className}`.trim()}
      style={{ color: `var(${COR[tom]})`, ...(rest['style'] as object) }}
    >
      {children}
    </span>
  )
}

/**
 * O tamanho é uma escala, não um número livre.
 *
 * `clamp` e não breakpoint: o mesmo componente serve 390px e desktop sem que
 * ninguém precise lembrar de uma media query — e o herói nunca quebra em duas
 * linhas por causa de um `R$ 4.287,63` que não coube.
 */
const TAMANHO = {
  grande: 'text-[clamp(2rem,9vw,3.25rem)]',
  medio: 'text-[clamp(1.5rem,5.5vw,2rem)]',
  pequeno: 'text-[clamp(1.125rem,4vw,1.375rem)]',
} as const

/** Ver a seção "a cor significa uma coisa só" em tokens.css. */
const COR = {
  texto: '--text',
  positivo: '--positive',
  negativo: '--negative',
  economia: '--economia',
  pendente: '--pendente',
} as const
